import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Radio, message, Tooltip, Progress, Typography, Alert, Spin, Popconfirm, Empty, Drawer, Grid, Dropdown, Collapse } from 'antd';
import { PlusOutlined, TeamOutlined, ReloadOutlined, KeyOutlined, MailOutlined, RocketOutlined, SafetyCertificateOutlined, DeleteOutlined, MobileOutlined, GlobalOutlined, MoreOutlined, CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';
import ClientSetupDrawer from './ClientSetupDrawer';
import DomainSettingsDrawer from './DomainSettingsDrawer';
import DnsOneClick from '../../components/DnsOneClick';
import { useNavigate } from 'react-router-dom';
import webmailService from '../../services/webmailService';
import apiClient from '../../../shared/services/apiClient';
import { userPath } from '../../../shared/config/paths';
import { formatMoney, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import RenewAction from '../../../shared/components/RenewAction';
import { useRowContextMenu } from '../../../shared/components/RowActions';

const { Text } = Typography;

const ok = (res) => res && (res.code === 0 || res.success === true);

const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const DNS_ITEMS = [
    { key: 'mx', label: 'MX' },
    { key: 'spf', label: 'SPF' },
    { key: 'dkim', label: 'DKIM' },
    { key: 'dmarc', label: 'DMARC' },
];

const Domains = () => {
    const { t } = useTranslation();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const navigate = useNavigate();

    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dnsDomain, setDnsDomain] = useState(null);
    const [dnsStatus, setDnsStatus] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [dkimLoading, setDkimLoading] = useState(false);
    const [dnsKey, setDnsKey] = useState(0); // bump to reload the one-click panel
    const [clientSetupDomain, setClientSetupDomain] = useState(null);
    const [settingsDomain, setSettingsDomain] = useState(null);

    // legacy domains without a subscription
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [assignDomain, setAssignDomain] = useState(null);
    const [assignProductId, setAssignProductId] = useState(null);
    const [assignBillingCycle, setAssignBillingCycle] = useState('monthly');
    const [assignSubmitting, setAssignSubmitting] = useState(false);

    const fetchDomains = useCallback(async () => {
        setLoading(true);
        const res = await webmailService.getDomains();
        if (ok(res)) setDomains(Array.isArray(res.data) ? res.data : []);
        else message.error(res?.message || res?.error || t('Failed to load'));
        setLoading(false);
    }, [t]);

    useEffect(() => { fetchDomains(); }, [fetchDomains]);

    const openDns = (domain) => {
        setDnsDomain(domain);
        setDnsStatus(domain.dns || null);
        setDnsKey(k => k + 1);
    };

    const verifyDns = async (domain, quiet = false) => {
        if (!domain) return;
        setVerifying(true);
        const res = await webmailService.verifyDns(domain.local_id);
        setVerifying(false);
        if (!ok(res)) {
            if (!quiet) message.error(res?.message || res?.error || t('Verification failed'));
            return;
        }
        const data = res.data || {};
        setDnsStatus({ ...(data.status || {}), ...(data.results || {}), fully_verified: !!data.all_verified });
        if (data.ownership?.verified && dnsDomain && dnsDomain.local_id === domain.local_id) {
            setDnsDomain(prev => prev ? { ...prev, dns: { ...(prev.dns || {}), ownership_verified: true } } : prev);
        }
        if (!quiet) {
            if (data.ownership && !data.ownership.verified) message.warning(t('Ownership not verified yet: add the verification record and try again once DNS is live'));
            else if (data.all_verified) message.success(t('All DNS records are live'));
            else {
                const failed = Object.entries(data.results || {}).filter(([, v]) => !v).map(([k]) => k.toUpperCase());
                message.warning(t('Not live yet: {{list}}', { list: failed.join(', ') }));
            }
        }
        fetchDomains();
    };

    const rotateDkim = async () => {
        if (!dnsDomain) return;
        setDkimLoading(true);
        try {
            const res = await apiClient.post(`/api/user/webmail/domains/${dnsDomain.local_id}/dkim`);
            if (ok(res.data)) {
                message.success(t('DKIM key regenerated. Please update your DNS records.'));
                setDnsKey(k => k + 1);
            } else {
                message.error(res.data?.message || res.data?.error || t('Failed to generate DKIM'));
            }
        } catch {
            message.error(t('Failed to generate DKIM'));
        } finally {
            setDkimLoading(false);
        }
    };

    const deleteDomain = async (domain) => {
        const res = await webmailService.deleteDomain(domain.local_id);
        if (ok(res)) { message.success(t('Deleted')); fetchDomains(); }
        else message.error(res?.message || res?.error || t('Deletion failed'));
    };

    const openAssign = async (domain) => {
        setAssignDomain(domain);
        setAssignBillingCycle('monthly');
        let list = products;
        if (!list.length) {
            setProductsLoading(true);
            try {
                const res = await apiClient.get('/products', { params: { product_type: 'yeapmail', status: 'active' } });
                list = res.data?.success && Array.isArray(res.data?.data) ? res.data.data : [];
                setProducts(list);
            } catch { /* shown as empty list */ }
            setProductsLoading(false);
        }
        setAssignProductId(list[0]?.id || null);
    };

    const submitAssign = async () => {
        if (!assignProductId) { message.error(t('Select a plan')); return; }
        setAssignSubmitting(true);
        const res = await webmailService.assignSubscription(assignDomain.local_id, { product_id: assignProductId, billing_cycle: assignBillingCycle });
        setAssignSubmitting(false);
        if (!ok(res)) { message.error(res?.message || res?.error || t('Operation failed')); return; }
        message.success(t('Plan assigned'));
        setAssignDomain(null);
        fetchDomains();
        if (res.data?.invoice_id && res.data?.amount > 0) {
            Modal.confirm({
                title: t('Payment required'),
                content: t('Invoice #{{no}} created for {{amount}}. Pay now?', { no: res.data.invoice_number || res.data.invoice_id, amount: formatMoney(res.data.amount, res.data.currency || PLATFORM_CURRENCY, { original: true }) }),
                okText: t('Pay now'),
                cancelText: t('Later'),
                onOk: () => navigate(userPath(`/billing/invoices/${res.data.invoice_id}`)),
            });
        }
    };

    const goUsers = (d) => navigate(userPath(`/webmail/users?domain_id=${d.local_id}&domain_name=${encodeURIComponent(d.name)}`));

    const statusTag = (d) => {
        if (d.dns && d.dns.ownership_verified === false) return <Tag color="orange">{t('Not activated')}</Tag>;
        if (d.active) return <Tag color="green">{t('Normal')}</Tag>;
        if (d.status === 'pending') return <Tag color="orange">{t('Not yet effective')}</Tag>;
        if (d.status === 'suspended') return <Tag color="red">{t('Paused')}</Tag>;
        return <Tag color="red">{t('Disabled')}</Tag>;
    };

    const dnsTag = (d) => d.dns && d.dns.ownership_verified === false
        ? <Tag color="red" icon={<ExclamationCircleFilled />} style={{ cursor: 'pointer' }} onClick={() => openDns(d)}>{t('Ownership unverified')}</Tag>
        : (d.dns?.fully_verified || d.dns?.verified)
        ? <Tag color="green" icon={<CheckCircleFilled />}>{t('DNS live')}</Tag>
        : <Tag color="orange" icon={<ExclamationCircleFilled />} style={{ cursor: 'pointer' }} onClick={() => openDns(d)}>{t('DNS setup needed')}</Tag>;

    const planCell = (d) => {
        if (!d.subscription_id) {
            return (
                <Space size={4} wrap>
                    <Tag>{t('Legacy')}</Tag>
                    <Button size="small" type="link" style={{ padding: 0 }} onClick={() => openAssign(d)}>{t('Select a plan')}</Button>
                </Space>
            );
        }
        const expired = d.billing?.expired;
        return (
            <Space size={4} wrap>
                {d.product_name ? <Tag color={expired ? 'default' : 'blue'}>{t(d.product_name)}</Tag> : d.pending_product_name ? <Tag>{t(d.pending_product_name)}</Tag> : null}
                <RenewAction billing={d.billing} resourceName={d.domain_name || d.name} compact />
                {!expired && !d.billing?.pending && (
                    <Button size="small" type="link" style={{ padding: 0 }} icon={<RocketOutlined />} onClick={() => navigate(userPath(`/webmail/plans?domain_id=${d.local_id}`))}>{t('Upgrade')}</Button>
                )}
            </Space>
        );
    };

    const usersBar = (d) => {
        const used = d.current_users || 0;
        const max = Number(d.max_users);
        const unlimited = max < 0;
        const cap = unlimited ? null : (max || 100);
        const text = `${used}/${unlimited ? t('Unlimited') : cap}`;
        return <Progress percent={unlimited ? 0 : Math.round((used / cap) * 100)} status={!unlimited && used > cap ? 'exception' : undefined} size="small" format={() => text} />;
    };

    const storageBar = (d) => {
        const pct = Math.round(((d.used_bytes || 0) / (d.quota_bytes || 1)) * 100);
        return (
            <Tooltip title={`${formatBytes(d.used_bytes || 0)} / ${formatBytes(d.quota_bytes || 0)}`}>
                <Progress percent={pct} size="small" status={pct > 90 ? 'exception' : 'normal'} />
            </Tooltip>
        );
    };

    const moreMenu = (d) => ({
        items: [
            { key: 'dns', icon: <KeyOutlined />, label: t('DNS settings'), onClick: () => openDns(d) },
            { key: 'client', icon: <MobileOutlined />, label: t('Client settings'), onClick: () => setClientSetupDomain(d) },
            { key: 'alias', icon: <GlobalOutlined />, label: t('Domain settings'), onClick: () => setSettingsDomain(d) },
            { type: 'divider' },
            {
                key: 'delete', icon: <DeleteOutlined />, danger: true, label: t('Delete domain'),
                onClick: () => Modal.confirm({
                    title: t('Delete domain {{name}}?', { name: d.name }),
                    content: t('The domain and all its mailboxes will be deleted. This cannot be undone.'),
                    okText: t('Delete'), okButtonProps: { danger: true }, cancelText: t('Cancel'),
                    onOk: () => deleteDomain(d),
                }),
            },
        ],
    });

    // 行右键菜单：邮箱 + 「⋯」里的同一组操作
    const getRowActions = (d) => [
        { key: 'users', icon: <TeamOutlined />, label: t('Mailboxes'), onClick: () => goUsers(d) },
        ...moreMenu(d).items,
    ];
    const rowMenu = useRowContextMenu(getRowActions, { rowKey: 'local_id' });

    const columns = [
        {
            title: t('Domains'), dataIndex: 'name',
            render: (text, d) => (
                <Space direction="vertical" size={2}>
                    <Space size={6}><MailOutlined /><strong>{text}</strong></Space>
                    <Space size={4} wrap>{statusTag(d)}{dnsTag(d)}</Space>
                </Space>
            ),
        },
        { title: t('Plan'), width: 260, render: (_, d) => planCell(d) },
        { title: t('User'), width: 120, render: (_, d) => usersBar(d) },
        { title: t('Storage'), width: 140, render: (_, d) => storageBar(d) },
        {
            title: t('Actions'), width: 200, fixed: 'right',
            render: (_, d) => (
                <Space size={4}>
                    <Button size="small" icon={<TeamOutlined />} onClick={() => goUsers(d)}>{t('Mailboxes')}</Button>
                    <Tooltip title={t('DNS settings')}><Button size="small" icon={<KeyOutlined />} onClick={() => openDns(d)} /></Tooltip>
                    <Dropdown menu={moreMenu(d)} trigger={['click']}><Button size="small" icon={<MoreOutlined />} /></Dropdown>
                </Space>
            ),
        },
    ];

    const mobileList = (
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
            {loading && !domains.length && <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>}
            {!loading && !domains.length && <Empty description={t('No mail domains yet')} />}
            {domains.map(d => (
                <Card key={d.local_id} size="small" styles={{ body: { padding: 12 } }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                        <div style={{ minWidth: 0 }}>
                            <Text strong style={{ wordBreak: 'break-all' }}>{d.name}</Text>
                            <div style={{ marginTop: 4 }}><Space size={4} wrap>{statusTag(d)}{dnsTag(d)}</Space></div>
                        </div>
                        <Dropdown menu={moreMenu(d)} trigger={['click']}><Button icon={<MoreOutlined />} aria-label={t('More')} /></Dropdown>
                    </div>
                    <div style={{ marginTop: 8 }}>{planCell(d)}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr', alignItems: 'center', columnGap: 8, marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{t('User')}</Text>{usersBar(d)}
                        <Text type="secondary" style={{ fontSize: 12 }}>{t('Storage')}</Text>{storageBar(d)}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <Button block icon={<TeamOutlined />} onClick={() => goUsers(d)}>{t('Mailboxes')}</Button>
                        <Button block icon={<KeyOutlined />} onClick={() => openDns(d)}>{t('DNS settings')}</Button>
                    </div>
                </Card>
            ))}
        </Space>
    );

    const verifiedMap = dnsStatus || {};
    return (
        <Card
            title={t('Email domains')}
            styles={isMobile ? { body: { padding: 12 } } : undefined}
            extra={
                <Space>
                    <Tooltip title={t('Refresh')}><Button icon={<ReloadOutlined />} onClick={fetchDomains} loading={loading} /></Tooltip>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(userPath('/provision/yeapmail'))}>{isMobile ? null : t('Add domain')}</Button>
                </Space>
            }
        >
            {isMobile ? mobileList : (
                <Table
                    columns={columns}
                    dataSource={domains}
                    rowKey="local_id"
                    onRow={rowMenu.onRow()}
                    loading={loading}
                    scroll={{ x: 900 }}
                    locale={{ emptyText: <Empty description={t('No mail domains yet. Click "Add domain" at the top right.')} /> }}
                    pagination={{ pageSize: 10, hideOnSinglePage: true }}
                />
            )}
            {rowMenu.menu}

            <Drawer
                title={<span style={{ wordBreak: 'break-all' }}>{t('DNS settings · {{name}}', { name: dnsDomain?.name || '' })}</span>}
                open={!!dnsDomain}
                onClose={() => setDnsDomain(null)}
                width={isMobile ? '100%' : 860}
                destroyOnClose
                extra={
                    <Button type="primary" icon={<SafetyCertificateOutlined />} loading={verifying} onClick={() => verifyDns(dnsDomain)}>
                        {t('Verify')}
                    </Button>
                }
            >
                {dnsDomain && (
                    <>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                            {DNS_ITEMS.map(it => (
                                <Tag key={it.key} color={verifiedMap[it.key] ? 'green' : 'default'} icon={verifiedMap[it.key] ? <CheckCircleFilled /> : null}>
                                    {it.label}
                                </Tag>
                            ))}
                            {verifiedMap.fully_verified || verifiedMap.verified
                                ? <Text type="success">{t('Mail is ready to send and receive')}</Text>
                                : <Text type="secondary">{t('Add the records, then click "Verify"')}</Text>}
                        </div>

                        {dnsDomain.dns?.ownership_verified === false && verifiedMap.ownership_verified !== true && (
                            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                                message={t('Mail is enabled once you prove you own the domain: add the "Ownership verification" record (or point MX to us), then click "Verify"')} />
                        )}
                        <DnsOneClick
                            key={`mail-${dnsDomain.local_id}-${dnsKey}`}
                            app="webmail"
                            id={dnsDomain.local_id}
                            title={t('Mail records')}
                            onApplied={() => setTimeout(() => verifyDns(dnsDomain, true), 3000)}
                        />

                        <div style={{ marginTop: 20 }}>
                            <DnsOneClick
                                key={`client-${dnsDomain.local_id}-${dnsKey}`}
                                app="webmail_client"
                                id={dnsDomain.local_id}
                                title={t('Mail client autoconfig (optional)')}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
                            <Button icon={<MobileOutlined />} onClick={() => { const d = dnsDomain; setDnsDomain(null); setClientSetupDomain(d); }}>{t('Client settings')}</Button>
                        </div>

                        <Collapse
                            ghost
                            style={{ marginTop: 12 }}
                            items={[{
                                key: 'adv',
                                label: <Text type="secondary">{t('Advanced')}</Text>,
                                children: (
                                    <Popconfirm
                                        title={t('Regenerate DKIM')}
                                        description={t('The old key stops working immediately; update the DKIM record in DNS')}
                                        okText={t('Confirm')} cancelText={t('Cancel')}
                                        onConfirm={rotateDkim}
                                    >
                                        <Button icon={<KeyOutlined />} loading={dkimLoading}>{t('Regenerate DKIM')}</Button>
                                    </Popconfirm>
                                ),
                            }]}
                        />
                    </>
                )}
            </Drawer>

            <Modal
                title={t('Choose plan · {{name}}', { name: assignDomain?.name || '' })}
                open={!!assignDomain}
                onCancel={() => setAssignDomain(null)}
                onOk={submitAssign}
                confirmLoading={assignSubmitting}
                okText={t('Confirm')}
                cancelText={t('Cancel')}
                width={isMobile ? '100%' : 500}
                style={isMobile ? { top: 12, maxWidth: 'calc(100vw - 24px)' } : undefined}
            >
                <Radio.Group value={assignBillingCycle} onChange={e => setAssignBillingCycle(e.target.value)} style={{ marginBottom: 12 }}>
                    <Radio.Button value="monthly">{t('Monthly billing')}</Radio.Button>
                    <Radio.Button value="quarterly">{t('Quarterly')}</Radio.Button>
                    <Radio.Button value="annually">{t('Yearly')}</Radio.Button>
                </Radio.Group>
                {productsLoading ? <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div> : (
                    products.length === 0 ? <Alert type="warning" showIcon message={t('No plans available')} /> : products.map(p => {
                        const price = p.pricing?.find(x => x.billing_cycle === assignBillingCycle && x.enabled);
                        const selected = assignProductId === p.id;
                        return (
                            <Card key={p.id} size="small" hoverable onClick={() => setAssignProductId(p.id)}
                                style={{ marginBottom: 8, borderColor: selected ? '#1677ff' : undefined, borderWidth: selected ? 2 : 1 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                    <Text strong>{t(p.name)}</Text>
                                    <Text strong style={{ color: '#1677ff' }}>{price ? formatMoney(price.price, price.currency) : '—'}</Text>
                                </div>
                            </Card>
                        );
                    })
                )}
            </Modal>

            <DomainSettingsDrawer open={!!settingsDomain} domain={settingsDomain} onClose={() => setSettingsDomain(null)} />
            <ClientSetupDrawer open={!!clientSetupDomain} domain={clientSetupDomain} onClose={() => setClientSetupDomain(null)} />
        </Card>
    );
};

export default Domains;
